import { useEffect, useState } from 'react';
import { Button, Flex, Box, Text } from '@tonic-one/react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { PRODUCT_NAME } from 'src/utils/const';

/**
 * AAD admin-consent redirect target for the "Cloud Email Protection Smart
 * Alert" Entra ID application. Never opened directly by an admin — only hit as the
 * redirect_uri after AAD's adminconsent flow finishes.
 *
 * Same-origin localStorage bridge to AdminConsentBanner:
 *   AdminConsentBanner navigates the admin to a BFF-built kickoff URL in a
 *   new tab. AAD redirects back here with one of three outcomes:
 *     - admin_consent=True&tenant=<tid>           → status: 'approved'
 *     - error=access_denied&error_description=... → status: 'rejected'
 *     - error=<other>&error_description=...       → status: 'error'
 *   We persist the outcome to localStorage under
 *   `cas_smart_alert_consent_outcome`. AdminConsentBanner (same origin,
 *   polling) consumes the entry, calls authenticated POST
 *   /smartAlert/adminConsent/ack on cas-bff, then clears it.
 *
 * Why localStorage, not direct /ack here:
 *   This page is unauthenticated (no CAS session cookie); the banner already
 *   has orgId from useOrgStore and the session, so it owns the write.
 */

// Same keys the AdminConsentBanner uses. Bumping these strings requires
// changing the banner in lockstep — do a global search before renaming.
const OUTCOME_KEY = 'cas_smart_alert_consent_outcome';
const EXPECTED_KEY = 'cas_smart_alert_consent_expected';
const T_PREFIX = 'globalSetting.misdirectedEmailDetection.adminConsentRedirect';

type Outcome =
  | { status: 'approved'; tenant: string; state: string; ts: number }
  | {
      status: 'rejected' | 'error';
      error: string;
      errorDescription: string;
      state: string;
      ts: number;
    };

type ResultType = 'success' | 'rejected' | 'error' | 'missing';

interface ExpectedConsent {
  tenantId: string;
  state: string;
  ts?: number;
}

function writeOutcome(payload: Outcome) {
  try {
    localStorage.setItem(OUTCOME_KEY, JSON.stringify(payload));
  } catch (e) {
    // localStorage unavailable (private mode / quota) — surface in console
    // so the operator can fall back to the in-app re-check button.
    console.warn('[admin-consent] writeOutcome failed:', e);
  }
}

function readExpectedConsent(): ExpectedConsent | null {
  try {
    const raw = localStorage.getItem(EXPECTED_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ExpectedConsent;
    if (!parsed?.tenantId || !parsed?.state) return null;
    return parsed;
  } catch {
    return null;
  }
}

function normalizeTenantId(tenantId: string): string {
  return tenantId.trim().toLowerCase();
}

function getResultTheme(type: ResultType) {
  if (type === 'success') {
    return {
      icon: '✓',
      iconBg: '#10B981',
      iconShadow: 'rgba(16, 185, 129, 0.28)',
      infoBg: '#EFF8FF',
      infoBorder: '#CFE7FF',
      infoColor: '#2B5FB8',
    };
  }
  if (type === 'rejected' || type === 'missing') {
    return {
      icon: '!',
      iconBg: '#F59E0B',
      iconShadow: 'rgba(245, 158, 11, 0.28)',
      infoBg: '#FFF7ED',
      infoBorder: '#FED7AA',
      infoColor: '#9A3412',
    };
  }
  return {
    icon: '×',
    iconBg: '#EF4444',
    iconShadow: 'rgba(239, 68, 68, 0.28)',
    infoBg: '#FEF2F2',
    infoBorder: '#FECACA',
    infoColor: '#991B1B',
  };
}

function AdminConsent() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const [isCopied, setIsCopied] = useState(false);

  const adminConsent = params.get('admin_consent');
  const tenant = params.get('tenant');
  const state = params.get('state') || '';
  const error = params.get('error');
  const errorDescription = params.get('error_description') || '';

  const expectedConsent = readExpectedConsent();
  const isSuccess = adminConsent === 'True' && !!tenant && !error;
  const isMissingParams = !error && !adminConsent;
  const tenantValidationError = isSuccess
    ? !expectedConsent
      ? 'tenant_validation_unavailable'
      : normalizeTenantId(tenant) !==
          normalizeTenantId(expectedConsent.tenantId)
        ? 'tenant_mismatch'
        : ''
    : '';
  const isValidatedSuccess = isSuccess && !tenantValidationError;
  const resultType: ResultType = isValidatedSuccess
    ? 'success'
    : isMissingParams
      ? 'missing'
      : error === 'access_denied'
        ? 'rejected'
        : 'error';
  const theme = getResultTheme(resultType);

  const productName = PRODUCT_NAME;
  const pluginName = t('common.nativeApp.smartAlertName');

  const tenantValidationMessage = tenantValidationError
    ? tenantValidationError === 'tenant_mismatch'
      ? t(`${T_PREFIX}.error.tenantMismatch`, {
          actualTenantId: tenant,
          expectedTenantId: expectedConsent?.tenantId || '',
        })
      : t(`${T_PREFIX}.error.tenantValidationUnavailable`, { productName })
    : '';

  useEffect(() => {
    if (error) {
      // access_denied is the canonical signal that the admin clicked Cancel
      // on the AAD UI. Anything else is an unexpected error (config, scope,
      // etc.) that the banner should surface verbatim.
      writeOutcome({
        status: error === 'access_denied' ? 'rejected' : 'error',
        error,
        errorDescription,
        state,
        ts: Date.now(),
      });
    } else if (isSuccess && tenant) {
      if (tenantValidationError) {
        writeOutcome({
          status: 'error',
          error: tenantValidationError,
          errorDescription: tenantValidationMessage,
          state,
          ts: Date.now(),
        });
      } else {
        writeOutcome({
          status: 'approved',
          tenant,
          state,
          ts: Date.now(),
        });
      }
    }
    // missingParams: no localStorage write (admin landed here directly via
    // bookmark or direct URL — banner has nothing to ack).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCopyTenant = async () => {
    if (!tenant) return;
    try {
      await navigator.clipboard?.writeText(tenant);
      setIsCopied(true);
      window.setTimeout(() => setIsCopied(false), 2000);
    } catch {
      setIsCopied(false);
    }
  };

  const currentYear = new Date().getFullYear();
  const heading = isValidatedSuccess
    ? t(`${T_PREFIX}.success.heading`)
    : isMissingParams
      ? t(`${T_PREFIX}.error.missingParamsHeading`)
      : error === 'access_denied'
        ? t(`${T_PREFIX}.error.rejectedHeading`)
        : t(`${T_PREFIX}.error.heading`);

  return (
    <Flex
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      p="24px"
      style={{
        background:
          'radial-gradient(circle at 82% 82%, rgba(255, 119, 210, 0.9), transparent 28%), linear-gradient(135deg, #36A9E8 0%, #6B57BF 44%, #A259C7 70%, #F06BD4 100%)',
      }}
    >
      <Flex
        direction="column"
        alignItems="center"
        width="100%"
        maxWidth="620px"
        px="44px"
        py="40px"
        borderRadius="24px"
        bg="#FBFAFF"
        boxShadow="0 28px 80px rgba(35, 20, 86, 0.24)"
      >
        <Flex alignItems="center" gap="8px" mb="20px">
          <Text
            color="#171827"
            fontSize="12px"
            fontWeight="bold"
            letterSpacing="0.08em"
          >
            {t(`${T_PREFIX}.brandPrefix`)}
            {` ${productName}`}
          </Text>
        </Flex>

        <Flex
          alignItems="center"
          justifyContent="center"
          width="86px"
          height="86px"
          borderRadius="50%"
          mb="22px"
          bg="rgba(16, 185, 129, 0.12)"
          boxShadow={`0 0 0 12px ${theme.iconShadow}`}
        >
          <Flex
            alignItems="center"
            justifyContent="center"
            width="68px"
            height="68px"
            borderRadius="50%"
            bg={theme.iconBg}
            color="white"
            fontSize="42px"
            fontWeight="bold"
            lineHeight="1"
          >
            {theme.icon}
          </Flex>
        </Flex>

        <Text
          as="h1"
          m="0"
          mb="12px"
          color="#171827"
          fontSize="28px"
          fontWeight="bold"
          textAlign="center"
        >
          {heading}
        </Text>

        {isValidatedSuccess && tenant ? (
          <>
            <Text
              maxWidth="480px"
              color="#697386"
              fontSize="15px"
              lineHeight="1.55"
              textAlign="center"
              mb="26px"
            >
              {t(`${T_PREFIX}.success.bodyBefore`)}
              <strong>{pluginName}</strong>
              {t(`${T_PREFIX}.success.bodyAfter`)}
            </Text>

            <Flex
              alignItems="center"
              width="100%"
              p="14px 16px"
              mb="24px"
              border="1px solid #E6DEFF"
              borderRadius="14px"
              bg="#F4F0FF"
            >
              <Flex
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
                width="34px"
                height="34px"
                borderRadius="10px"
                bg="white"
                color="#8A63D2"
                fontSize="18px"
                mr="14px"
              >
                ⌂
              </Flex>
              <Box flex="1" minWidth={0}>
                <Text
                  color="#8A63D2"
                  fontSize="11px"
                  fontWeight="bold"
                  letterSpacing="0.08em"
                  textTransform="uppercase"
                >
                  {t(`${T_PREFIX}.success.tenantIdLabel`)}
                </Text>
                <Text
                  color="#4B4A5F"
                  fontFamily="monospace"
                  fontSize="13px"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                >
                  {tenant}
                </Text>
              </Box>
              <Button
                size="sm"
                variant="secondary"
                ml="12px"
                color="#171827"
                onClick={handleCopyTenant}
              >
                {t(
                  `${T_PREFIX}.success.${isCopied ? 'copiedButton' : 'copyButton'}`
                )}
              </Button>
            </Flex>
          </>
        ) : (
          <Box width="100%" mb="24px">
            {!isMissingParams && tenantValidationMessage && (
              <Text color="#697386" fontSize="14px" lineHeight="1.55" mb="16px">
                {tenantValidationMessage}
              </Text>
            )}
            {!isMissingParams && (
              <Box
                p="14px 16px"
                mb="16px"
                border="1px solid #FECACA"
                borderRadius="14px"
                bg="#FEF2F2"
              >
                <Text color="#991B1B" fontWeight="semibold" mb="6px">
                  {t(`${T_PREFIX}.error.label`)}{' '}
                  <code>{tenantValidationError || error}</code>
                </Text>
                {(tenantValidationMessage || errorDescription) && (
                  <Text color="#7F1D1D" fontSize="13px" lineHeight="1.5">
                    {tenantValidationMessage || errorDescription}
                  </Text>
                )}
              </Box>
            )}
          </Box>
        )}

        <Flex
          width="100%"
          alignItems="flex-start"
          p="16px"
          border={`1px solid ${theme.infoBorder}`}
          borderRadius="14px"
          bg={theme.infoBg}
          color={theme.infoColor}
        >
          <Flex
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
            width="20px"
            height="20px"
            borderRadius="50%"
            border={`2px solid ${theme.infoColor}`}
            fontSize="12px"
            fontWeight="bold"
            mr="12px"
            mt="2px"
          >
            i
          </Flex>
          <Text fontSize="14px" lineHeight="1.55">
            {isValidatedSuccess
              ? t(`${T_PREFIX}.success.followupHint`, { productName })
              : isMissingParams
                ? t(`${T_PREFIX}.error.missingParams`, { productName })
                : t(`${T_PREFIX}.error.followupHint`, { productName })}
          </Text>
        </Flex>

        <Text color="#A0A7B8" fontSize="12px" mt="28px">
          {t(`${T_PREFIX}.footer`, { year: currentYear })}
        </Text>
      </Flex>
    </Flex>
  );
}

export default AdminConsent;
